/** @file The runtime's mutable state shared by its helpers (one per mounted viewport, never in React). */
import type { Doc, Id } from '@pose2d/format/types';
import type { RenderBuffers } from '@pose2d/geometry/types';
import type { RgbaImage, Vec2 } from '@pose2d/math/types';
import type { FontAtlas } from '@pose2d/overlay/types';
import type { ViewportRenderer } from '@pose2d/renderer-webgl/types';
import type { PendingCommand } from '../../stores/types';
import type { PreviewValues } from './scene/channelOffset';
import type { Scene } from './scene/createScene';
import type { GhostScene } from './scene/ghostScene';
import type { PageSet } from './scene/pageSet';
import type { SceneMaps } from './scene/sceneMaps';
import type { GizmoDrawing, ToolGesture } from './viewportTypes';

/** Everything injected: the renderer, the font page, image bytes, the commit (→ created name → id, {} when refused; W3-11). */
export interface RuntimeDeps { renderer: ViewportRenderer; font: FontAtlas; fontImage: RgbaImage; fetchPng: (sha256: string) => Promise<Uint8Array | null>; commit: (batch: PendingCommand[]) => Readonly<Record<string, Id>> }

/** `base` is the preview held when the gesture started (the unkeyed pose); `preview` = base + the gesture's; `ghost` = ghostStore's, built. */
export interface RuntimeState {
  deps: RuntimeDeps; width: number; height: number; doc: Doc | null; scene: Scene | null; maps: SceneMaps | null; pages: PageSet | null; ghost: GhostScene | null;
  images: Map<string, RgbaImage | null>; preview: Map<Id, PreviewValues>; base: Map<Id, PreviewValues>;
  gesture: ToolGesture | null; pan: Vec2 | null; gizmo: GizmoDrawing | null; dirty: boolean; chunks: RenderBuffers[];
}

/** Nothing loaded yet; dirty, so the first frame paints the background. */
export function createRuntimeState(deps: RuntimeDeps): RuntimeState {
  return { deps, width: 0, height: 0, doc: null, scene: null, maps: null, pages: null, ghost: null, images: new Map(), preview: new Map(), base: new Map(), gesture: null, pan: null, gizmo: null, dirty: true, chunks: [] };
}
